
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { Logger } from "@/lib/utils/logger";

const logger = new Logger('useAdmin');

export function useAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (authLoading) {
        setLoading(true);
        return;
      }
      
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // First check user metadata for admin role in the roles array
        const userRoles = user.user_metadata?.roles || [];
        if (Array.isArray(userRoles) && userRoles.includes('admin')) {
          setIsAdmin(true);
          setLoading(false);
          return;
        }
        
        // If not in roles array, check single role field (legacy format)
        if (user.user_metadata?.role === 'admin') {
          setIsAdmin(true);
          setLoading(false);
          return;
        }

        // If neither of the above worked, check the database function - fixed JSON parameter passing
        const { data, error } = await supabase.rpc('user_has_role', {
          user_id: user.id,
          role_name: 'admin'
        });

        if (error) {
          logger.error('Error checking admin role:', error);
          setError('Failed to verify admin status');
          setIsAdmin(false);
        } else {
          setIsAdmin(!!data);
        }
      } catch (err) {
        logger.error('Error in admin check:', err);
        setError('An unexpected error occurred');
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user?.id, authLoading]);

  return {
    isAdmin,
    loading,
    error
  };
}
